import { CartItem } from "./CartItem.js";
/*
    Shared shopping-cart logic for adding / matching / removing items.

    The list of items is kept as CartItem instances in `this.items`. All
    mutations happen IN PLACE (push / splice / field assignment) — `this.items`
    is never reassigned — so the array can be bound to a framework's reactive
    proxy (e.g. Vue `reactive()`) and stay tracked. Reassigning `this.items`
    would detach that proxy and silently break reactivity.
*/
export class Cart {
    items = [];
    constructor(data) {
        let rawItems = [];
        if (Array.isArray(data)) {
            rawItems = data;
        }
        else if (data && Array.isArray(data.items)) {
            rawItems = data.items;
        }
        for (let raw of rawItems) {
            if (!raw) {
                continue;
            }
            this.items.push(raw instanceof CartItem ? raw : new CartItem(raw.id, raw));
        }
    }
    /*
        Relevant-info hash for an entry that may be a CartItem instance or a
        plain compatible object (e.g. a cart hydrated from a saved order, which
        keeps display-only fields like subtotal/price that CartItem doesn't
        model). Normalizing through CartItem keeps the hash consistent either way.
    */
    static relevantInfoHash(item) {
        let cartItem = item instanceof CartItem ? item : new CartItem(item.id, item);
        return cartItem.getRelevantInfoHash();
    }
    getItemById(id) {
        if (!this.items || !id) {
            return null;
        }
        for (let item of this.items) {
            if (item.id == id) {
                return item;
            }
        }
        return null;
    }
    /*
        if it's an existing item (matched by id): find it and update its fields
        if it's a new item:
            check if there is an equivalent item (same relevant info)
            if yes: just increase the quantity
            if no:  add it as a new one
    */
    saveItem(item) {
        let incoming = item instanceof CartItem ? item : new CartItem(item.id, item);
        // Case A — edit existing item in place
        let existingItem = this.getItemById(incoming.id);
        if (existingItem) {
            existingItem.productId = incoming.productId;
            existingItem.note = incoming.note;
            existingItem.courseId = incoming.courseId;
            existingItem.quantity = incoming.quantity;
            existingItem.options = incoming.options;
            existingItem.chosen = incoming.chosen;
            return;
        }
        // Case B — an equivalent item already exists: just increase its quantity
        let incomingHash = incoming.getRelevantInfoHash();
        for (let stateItem of this.items) {
            if (Cart.relevantInfoHash(stateItem) === incomingHash) {
                stateItem.quantity = (stateItem.quantity || 0) + (incoming.quantity || 0);
                return;
            }
        }
        // Case C — genuinely new item: assign an id and push a detached copy
        let id = CartItem.generateId(incoming.productId);
        this.items.push(new CartItem(id, JSON.parse(JSON.stringify(incoming))));
    }
    deleteItem(item) {
        if (!item || !item.id) {
            return;
        }
        let existingItem = this.getItemById(item.id);
        if (existingItem) {
            let index = this.items.indexOf(existingItem);
            if (index >= 0) {
                this.items.splice(index, 1);
            }
        }
    }
    empty() {
        this.items.splice(0, this.items.length);
    }
}
